/**
 * DateInput — text input that displays + accepts `dd/mm/yyyy` while keeping
 * the underlying value in ISO (yyyy-mm-dd). Includes a native date picker icon
 * on the right that triggers a hidden <input type="date"> so the user still
 * gets a calendar UI but the visible value is always dd/mm/yyyy.
 *
 * Props mirror a controlled input:
 *   value     : ISO string (yyyy-mm-dd) or ''
 *   onChange  : (isoString) => void
 *   testid    : optional data-testid prefix
 */
import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { isoToDisplay, displayToIso } from '@/lib/dateFormat';
import { log } from '@/lib/log';

const DateInput = ({ value, onChange, testid, placeholder = 'dd/mm/yyyy', readOnly, className = '', ...rest }) => {
  const [text, setText] = useState(isoToDisplay(value));
  const hiddenRef = useRef(null);

  // Sync external value → text
  useEffect(() => {
    setText(isoToDisplay(value));
  }, [value]);

  const handleText = (e) => {
    // Always rebuild the dd/mm/yyyy mask from the digits alone, so separators
    // the user types themselves don't stack on top of the auto-inserted ones.
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let raw = digits;
    if (digits.length > 4) raw = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) raw = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(raw);
    if (digits.length === 8) {
      const iso = displayToIso(raw);
      if (iso) onChange?.(iso);
    } else if (digits.length === 0) {
      onChange?.('');
    }
  };

  const handleBlur = () => {
    const iso = displayToIso(text);
    if (text && !iso) {
      // Invalid → revert to last good value
      setText(isoToDisplay(value));
    }
  };

  const openPicker = () => {
    if (readOnly) return;
    const el = hiddenRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch (err) { log.debug('[dateinput] showPicker failed, using fallback:', err?.message || err); }
    }
    el.focus();
    el.click();
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        onChange={handleText}
        onBlur={handleBlur}
        readOnly={readOnly}
        data-testid={testid}
        className="pr-9"
        {...rest}
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        aria-label="Open date picker"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-ink/50 hover:text-ink hover:bg-surface-100"
        data-testid={testid ? `${testid}-picker` : undefined}
      >
        <Calendar className="w-4 h-4" />
      </button>
      <input
        ref={hiddenRef}
        type="date"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        className="absolute inset-0 opacity-0 pointer-events-none"
        tabIndex={-1}
      />
    </div>
  );
};

export default DateInput;
